import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, DialogActions, DialogContent, Typography } from '@mui/material';
import Dialog from '../components/StableDialog';
import DialogTitle from '../components/ui/FitDialogTitle';
import Button from '../components/ui/FitButton';
import { localize } from '../i18n';

// Consent belongs to this screen and account context, never to restored input or chat history.
export default function useAiDataConsent(scope) {
    const [open, setOpen] = useState(false);
    const decision = useRef(null);
    const pending = useRef(null);
    const finish = useCallback(accepted => {
        decision.current = accepted || scope === 'registration' ? accepted : null;
        const request = pending.current;
        pending.current = null;
        setOpen(false);
        request?.resolve(accepted);
    }, [scope]);
    const requestAiConsent = useCallback(() => {
        if (decision.current !== null) return Promise.resolve(decision.current);
        if (pending.current) return pending.current.promise;
        let resolve;
        const promise = new Promise(done => { resolve = done; });
        pending.current = { promise, resolve };
        setOpen(true);
        return promise;
    }, []);
    useEffect(() => {
        const reset = () => {
            const request = pending.current;
            pending.current = null;
            decision.current = null;
            setOpen(false);
            request?.resolve(false);
        };
        window.addEventListener('kkomok-account-context-changed', reset);
        return () => {
            window.removeEventListener('kkomok-account-context-changed', reset);
            pending.current?.resolve(false);
            pending.current = null;
            decision.current = null;
        };
    }, []);
    const aiConsentDialog = <Dialog open={open} onClose={() => finish(false)} fullWidth maxWidth="xs"
        aria-labelledby="ai-data-consent-title" aria-describedby="ai-data-consent-description" data-testid="ai-data-consent"
        PaperProps={{ sx: { borderRadius: 4, width: 'calc(100% - 32px)', maxWidth: 380, m: 2, maxHeight: 'calc(100% - 32px)',
            boxShadow: '0 16px 64px rgba(35, 30, 12, 0.16)' } }}>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
                <DialogTitle id="ai-data-consent-title" sx={{ p: 0, color: '#1a1a1a', fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.4 }}>{localize('AI 전송 동의')}</DialogTitle>
                <Typography sx={{ mt: 0.4, color: '#777', fontSize: '0.85rem', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                    {scope === 'chat' ? localize('AI 약 상담') : localize('약 자동 채움·상호작용 확인')}
                </Typography>
            </Box>
        </Box>
        <DialogContent sx={{ px: 2.5, py: 0, overflowWrap: 'anywhere', '&:first-of-type': { pt: 0 } }}>
            <Box sx={{ p: 2, mb: 1.75, borderRadius: 3, bgcolor: '#f7f7f5' }}>
            <Typography id="ai-data-consent-description" sx={{ color: '#444', fontSize: '0.9375rem', lineHeight: 1.7, wordBreak: 'keep-all' }}>{scope === 'chat'
                ? localize('질문과 대화 내용, 등록된 약의 이름·용량·복용법·시간·알림 중지 상태를 OpenAI에 전송해 상담 답변을 만들어요.')
                : localize('입력한 약 이름과 선택한 제품 정보, 기존에 등록한 약 이름·성분·복용 시간을 OpenAI에 전송해 자동 채움과 상호작용 확인에 사용해요.')}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.7, color: '#777', mb: 1, wordBreak: 'keep-all' }}>{localize('이름, 연락처, 처방전 사진 등 불필요한 개인정보는 입력하지 마세요. AI 답변은 참고용이며 의료 전문가의 판단을 대신하지 않아요.')}</Typography>
            <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.7, color: '#777', wordBreak: 'keep-all' }}>{localize('동의는 이 화면을 이용하는 동안만 유지해요. 취소하면 AI로 전송하지 않으며, 약을 직접 등록하고 알림을 사용할 수 있어요.')}</Typography>
            <Box component="a" href="/privacy" sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, mt: 0.5, color: '#686868', fontSize: '0.8125rem', lineHeight: 1.6, textUnderlineOffset: '3px',
                '&:focus-visible': { outline: '2px solid #8a701d', outlineOffset: 2, borderRadius: 1 } }}>{localize('개인정보 처리방침')}</Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pt: 1.5, pb: 2.5, flexDirection: 'column', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
            <Button fullWidth variant="contained" disableElevation onClick={() => finish(true)} sx={{ minHeight: 48, py: 1.25, borderRadius: 3, fontSize: '0.9375rem', fontWeight: 700,
                bgcolor: '#ffe566', color: '#5a4a00', '&:hover': { bgcolor: '#f4d94e' }, '&:focus-visible': { outline: '2px solid #8a701d', outlineOffset: 2 } }}>{localize('동의하고 AI 사용')}</Button>
            <Button fullWidth onClick={() => finish(false)} sx={{ minHeight: 44, py: 1.1, borderRadius: 3, fontSize: '0.9375rem', fontWeight: 600,
                bgcolor: '#f5f5f5', color: '#666', '&:hover': { bgcolor: '#ececec' }, '&:focus-visible': { outline: '2px solid #777', outlineOffset: 2 } }}>{localize('취소')}</Button>
        </DialogActions>
    </Dialog>;
    return { requestAiConsent, aiConsentDialog, aiConsentPending: open };
}
